// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';

const logger = new Logger('RoleOfferCommands');

function resolveMessageGuildId(message: Message): string | null {
	return Channels.getChannel(message.channelId)?.guildId ?? message.guildId ?? null;
}

export async function claimRoleOffer(channelId: string, messageId: string, roleId: string): Promise<void> {
	await http.put(Endpoints.CHANNEL_MESSAGE_ROLE_OFFER(channelId, messageId, roleId));
}

export async function unclaimRoleOffer(channelId: string, messageId: string, roleId: string): Promise<void> {
	await http.delete(Endpoints.CHANNEL_MESSAGE_ROLE_OFFER(channelId, messageId, roleId));
}

export async function toggleRoleOffer(message: Message, roleId: string): Promise<void> {
	const guildId = resolveMessageGuildId(message);
	const userId = Authentication.currentUserId;
	if (!guildId || !userId) {
		return;
	}
	const member = GuildMembers.getMember(guildId, userId);
	const hasRole = member?.roles.has(roleId) ?? false;
	try {
		if (hasRole) {
			await unclaimRoleOffer(message.channelId, message.id, roleId);
		} else {
			await claimRoleOffer(message.channelId, message.id, roleId);
		}
	} catch (error) {
		logger.error('Failed to toggle role offer', error);
		throw error;
	}
}

export async function sendMessageWithRoleOffers(
	channelId: string,
	content: string,
	roleOffers: Array<string>,
): Promise<void> {
	await MessageCommands.send(channelId, {
		content,
		nonce: SnowflakeUtils.fromTimestamp(Date.now()),
		hasAttachments: false,
		flags: 0,
		roleOffers,
	});
}

export async function setMessageRoleOffers(message: Message, roleOffers: Array<string>): Promise<void> {
	const updated = await MessageCommands.edit(
		message.channelId,
		message.id,
		undefined,
		undefined,
		undefined,
		undefined,
		roleOffers,
	);
	if (updated) {
		Messages.handleIncomingMessage({channelId: message.channelId, message: updated});
	}
}
